import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CircleCheckIcon, InfoIcon, LoaderCircleIcon, LockIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useContactForm } from '../../hooks/useContactForm';
import type { ErrorField, TimeWindow } from '../../hooks/useContactForm';
import { services } from '../../data/services';
import type { Language, Localized } from '../../types/content';
import { buttonStyles } from '../../utils/styles';
interface ContactFormProps {
  showScheduling?: boolean;
  idPrefix?: string;
}
const languages: {
  id: Language;
  label: string;
}[] = [{
  id: 'en',
  label: 'English'
}, {
  id: 'es',
  label: 'Español'
}];
const timeWindows: {
  id: TimeWindow;
  label: Localized;
}[] = [{
  id: '',
  label: {
    en: 'Any time',
    es: 'Cualquier hora'
  }
}, {
  id: 'morning',
  label: {
    en: 'Morning (8am–12pm)',
    es: 'Mañana (8am–12pm)'
  }
}, {
  id: 'afternoon',
  label: {
    en: 'Afternoon (12–5pm)',
    es: 'Tarde (12–5pm)'
  }
}, {
  id: 'evening',
  label: {
    en: 'Evening (5–8pm)',
    es: 'Noche (5–8pm)'
  }
}];
const errorMessages: Record<string, Localized> = {
  'firstName-required': {
    en: 'Please enter your first name.',
    es: 'Por favor escribe tu nombre.'
  },
  'lastName-required': {
    en: 'Please enter your last name.',
    es: 'Por favor escribe tu apellido.'
  },
  'email-required': {
    en: 'Please enter your email address.',
    es: 'Por favor escribe tu correo electrónico.'
  },
  'email-invalid': {
    en: 'Please enter a valid email address.',
    es: 'Escribe un correo electrónico válido.'
  },
  'phone-required': {
    en: 'Please enter a phone number.',
    es: 'Por favor escribe un número de teléfono.'
  },
  'phone-invalid': {
    en: 'Please enter a 10-digit phone number.',
    es: 'Escribe un número de 10 dígitos.'
  },
  'service-required': {
    en: 'Please choose a service.',
    es: 'Por favor elige un servicio.'
  }
};
const inputBase = 'mt-2 block w-full min-h-[52px] rounded-xl border bg-white px-4 text-base text-ink placeholder:text-ink-soft/70 transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:ring-4 focus:ring-brand-light/30';
export function ContactForm({
  showScheduling = false,
  idPrefix = 'cf'
}: ContactFormProps) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    values,
    errors,
    status,
    update,
    submit,
    reset
  } = useContactForm(lang, idPrefix);
  const fieldClass = (field?: ErrorField) => `${inputBase} ${field && errors[field] ? 'border-red-600' : 'border-[#CFD6DD] focus:border-brand'}`;
  const errorProps = (field: ErrorField) => ({
    'aria-invalid': !!errors[field],
    'aria-describedby': errors[field] ? `${idPrefix}-${field}-err` : undefined
  });
  const ErrorText = ({
    field
  }: {
    field: ErrorField;
  }) => errors[field] ? <p id={`${idPrefix}-${field}-err`} className="mt-1.5 text-sm font-medium text-red-700" data-id="element-264" data-mp="jvh3aruf5w1b">
        {t(errorMessages[`${field}-${errors[field]}`])}
      </p> : null;
  const Required = () => <span className="text-red-700" aria-hidden="true" data-id="element-265" data-mp="34kodbjue9vah">
      *
    </span>;
  return <AnimatePresence mode="wait" initial={false} data-id="element-266" data-mp="1k5i47591pv7d">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="flex flex-col items-center py-10 text-center" role="status" aria-live="polite" data-id="element-267" data-mp="1p4bkt6jmq2br">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sage-tint text-sage-dark" data-id="element-268" data-mp="27trp84yfqexg">
            <CircleCheckIcon className="h-9 w-9" aria-hidden="true" data-id="element-269" data-mp="2cscpg4dosylw" />
          </span>
          <h3 className="mt-5 text-2xl font-bold text-ink" data-id="element-270" data-mp="2hrp9yih35tlb">{t({
          en: 'Thank you!',
          es: '¡Gracias!'
        })}</h3>
          <p className="mt-2 max-w-md text-lg text-ink-soft" data-id="element-271" data-mp="if3dm5xo510h">
            {t({
          en: "We'll contact you within 24 hours to discuss your loved one's care needs.",
          es: 'Nos comunicaremos contigo en 24 horas para discutir las necesidades de cuidado de tu ser querido.'
        })}
          </p>
          <p className="mt-4 text-sm text-ink-soft" data-id="element-272" data-mp="28uvstxl2mzmu">
            {t({
          en: 'Need help sooner? Call (703) 231-7468 anytime.',
          es: '¿Necesitas ayuda antes? Llama al (703) 231-7468 a cualquier hora.'
        })}
          </p>
          <button type="button" onClick={reset} className={`${buttonStyles.outline} mt-7`} data-id="element-273" data-mp="2ivfx793w0k9a">
            {t({
          en: 'Send another request',
          es: 'Enviar otra solicitud'
        })}
          </button>
        </motion.div> : <motion.form key="form" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" aria-label={t({
      en: 'Request a free consultation',
      es: 'Solicitar una consulta gratuita'
    })} data-id="element-274" data-mp="1q7a9qelg8h5r">
          <div data-id="element-275" data-mp="17epp1kfwlid1">
            <label htmlFor={`${idPrefix}-firstName`} className="font-semibold text-ink" data-id="element-276" data-mp="17d52oi3qt5q9">
              {t({
            en: 'First name',
            es: 'Nombre'
          })} <Required data-id="element-277" data-mp="1229fr2d01ses" />
            </label>
            <input id={`${idPrefix}-firstName`} type="text" autoComplete="given-name" required {...errorProps('firstName')} value={values.firstName} onChange={e => update('firstName', e.target.value)} className={fieldClass('firstName')} data-id="element-278" data-mp="1232d5cq2j5k9" />
            <ErrorText field="firstName" data-id="element-279" data-mp="1l0vhmjgcbnjr" />
          </div>

          <div data-id="element-280" data-mp="1ky99ahlml40h">
            <label htmlFor={`${idPrefix}-lastName`} className="font-semibold text-ink" data-id="element-281" data-mp="163l5r8zscfni">
              {t({
            en: 'Last name',
            es: 'Apellido'
          })} <Required data-id="element-282" data-mp="1b1eeo9sh3nrh" />
            </label>
            <input id={`${idPrefix}-lastName`} type="text" autoComplete="family-name" required {...errorProps('lastName')} value={values.lastName} onChange={e => update('lastName', e.target.value)} className={fieldClass('lastName')} data-id="element-283" data-mp="1b48vzsg6wsgo" />
            <ErrorText field="lastName" data-id="element-284" data-mp="1xc5wnfrieepv" />
          </div>

          <div data-id="element-285" data-mp="1sjuyahcf30zf">
            <label htmlFor={`${idPrefix}-email`} className="font-semibold text-ink" data-id="element-286" data-mp="1sjctuxflpggf">
              {t({
            en: 'Email address',
            es: 'Correo electrónico'
          })} <Required data-id="element-287" data-mp="1nkrsguom4qqe" />
            </label>
            <input id={`${idPrefix}-email`} type="email" autoComplete="email" required {...errorProps('email')} value={values.email} onChange={e => update('email', e.target.value)} className={fieldClass('email')} data-id="element-288" data-mp="1ilov7wevjosz" />
            <ErrorText field="email" data-id="element-289" data-mp="14nqlj4bi0gjt" />
          </div>

          <div data-id="element-290" data-mp="33z1gfnoy0dgp">
            <label htmlFor={`${idPrefix}-phone`} className="font-semibold text-ink" data-id="element-291" data-mp="33zjnzgh22e3x">
              {t({
            en: 'Phone number',
            es: 'Número de teléfono'
          })} <Required data-id="element-292" data-mp="2z0zw6zfs683n" />
            </label>
            <input id={`${idPrefix}-phone`} type="tel" autoComplete="tel" inputMode="tel" required placeholder="(703) 555-0123" {...errorProps('phone')} value={values.phone} onChange={e => update('phone', e.target.value)} className={fieldClass('phone')} data-id="element-293" data-mp="36k97iy7ukgvc" />
            <ErrorText field="phone" data-id="element-294" data-mp="2nln6tkg8srry" />
          </div>

          <div className="sm:col-span-2" data-id="element-295" data-mp="2nmy73aahbheb">
            <label htmlFor={`${idPrefix}-service`} className="font-semibold text-ink" data-id="element-296" data-mp="2sj56slca7wnt">
              {t({
            en: 'Service interested in',
            es: 'Servicio de interés'
          })} <Required data-id="element-297" data-mp="2xl4bbyjrczmc" />
            </label>
            <select id={`${idPrefix}-service`} required {...errorProps('service')} value={values.service} onChange={e => update('service', e.target.value)} className={fieldClass('service')} data-id="element-298" data-mp="32ie7ry5kebjo">
              <option value="" disabled data-id="element-299" data-mp="12vek6ctde1j1">
                {t({
              en: 'Select a service…',
              es: 'Selecciona un servicio…'
            })}
              </option>
              {services.map(s => <option key={s.id} value={s.id} data-id="element-300" data-mp="1clr3k1pg7onl">
                  {t(s.name)}
                </option>)}
            </select>
            <ErrorText field="service" data-id="element-301" data-mp="17tzha3dbs539" />
          </div>

          <div className="sm:col-span-2" data-id="element-302" data-mp="1lmfe2mfmv6vq">
            <label htmlFor={`${idPrefix}-needs`} className="font-semibold text-ink" data-id="element-303" data-mp="1ll4c8s5r0979">
              {t({
            en: 'Tell us more',
            es: 'Cuéntanos más'
          })}{' '}
              <span className="font-normal text-ink-soft" data-id="element-304" data-mp="1vkldmxdyw0xf">{t({
              en: '(optional)',
              es: '(opcional)'
            })}</span>
            </label>
            <textarea id={`${idPrefix}-needs`} rows={4} placeholder={t({
          en: 'About your loved one, health conditions, hours you have in mind…',
          es: 'Sobre tu ser querido, condiciones de salud, horario que tienes en mente…'
        })} value={values.needs} onChange={e => update('needs', e.target.value)} className={`${fieldClass()} py-3`} data-id="element-305" data-mp="1ql8vur1ydk1p" />
          </div>

          {showScheduling && <>
              <div data-id="element-306" data-mp="1y2yn0o58sxmc">
                <label htmlFor={`${idPrefix}-date`} className="font-semibold text-ink" data-id="element-307" data-mp="2344i2smdkwz1">
                  {t({
              en: 'Preferred consultation day',
              es: 'Día preferido para la consulta'
            })}
                </label>
                <input id={`${idPrefix}-date`} type="date" value={values.preferredDate} min={new Date().toISOString().split('T')[0]} onChange={e => update('preferredDate', e.target.value)} className={fieldClass()} data-id="element-308" data-mp="1u36c44sdmfdt" />
              </div>
              <div data-id="element-309" data-mp="39kznngh81kue">
                <label htmlFor={`${idPrefix}-time`} className="font-semibold text-ink" data-id="element-310" data-mp="39idkru59ztii">
                  {t({
              en: 'Preferred time',
              es: 'Hora preferida'
            })}
                </label>
                <select id={`${idPrefix}-time`} value={values.preferredTime} onChange={e => update('preferredTime', e.target.value as TimeWindow)} className={fieldClass()} data-id="element-311" data-mp="2x5evm8314egm">
                  {timeWindows.map(w => <option key={w.id || 'any'} value={w.id} data-id="element-312" data-mp="2s6295og2l9ek">
                      {t(w.label)}
                    </option>)}
                </select>
              </div>
            </>}

          <fieldset className="sm:col-span-2" data-id="element-313" data-mp="35zthwulz8p98">
            <legend className="font-semibold text-ink" data-id="element-314" data-mp="35z0arsj3vxqk">{t({
            en: 'Language preference',
            es: 'Idioma preferido'
          })}</legend>
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-315" data-mp="3b0zfb5ql10p3">
              {languages.map(l => <label key={l.id} className="cursor-pointer" data-id="element-316" data-mp="1ujbq57tbrasx">
                  <input type="radio" name={`${idPrefix}-language`} value={l.id} checked={values.language === l.id} onChange={() => update('language', l.id)} className="peer sr-only" data-id="element-317" data-mp="1zkslyowyc72n" />
                  <span lang={l.id} className="inline-flex min-h-[48px] items-center rounded-full border-2 border-[#CFD6DD] px-5 font-medium text-ink transition-colors duration-150 ease-out hover:border-brand peer-checked:border-brand peer-checked:bg-brand-tint peer-checked:text-brand-dark peer-focus-visible:ring-4 peer-focus-visible:ring-brand-light/40" data-id="element-318" data-mp="qm9cdp9ic0it">
                    {l.label}
                  </span>
                </label>)}
            </div>
          </fieldset>

          {status === 'error' && <p className="flex items-start gap-2 rounded-xl bg-red-50 p-4 text-red-800 sm:col-span-2" role="alert" data-id="element-319" data-mp="3bbp88ynl2ffb">
              <InfoIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" data-id="element-320" data-mp="37b6davqe8t8w" />
              {t({
          en: 'Something went wrong sending your request. Please call (703) 231-7468.',
          es: 'Hubo un problema al enviar tu solicitud. Por favor llama al (703) 231-7468.'
        })}
            </p>}

          <div className="sm:col-span-2" data-id="element-321" data-mp="1ozf5eu16zatw">
            <button type="submit" disabled={status === 'submitting'} className={`${buttonStyles.primary} w-full text-lg disabled:cursor-wait disabled:opacity-80 sm:w-auto sm:px-8`} data-id="element-322" data-mp="1oydinsatczwc">
              {status === 'submitting' ? <>
                  <LoaderCircleIcon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-323" data-mp="119ayodspiuyo" />
                  {t({
              en: 'Sending…',
              es: 'Enviando…'
            })}
                </> : t({
            en: 'Request Free Consultation',
            es: 'Solicitar Consulta Gratuita'
          })}
            </button>
            <p className="mt-3 flex items-center gap-2 text-sm text-ink-soft" data-id="element-324" data-mp="2hmj3ef1vaow6">
              <LockIcon className="h-4 w-4 shrink-0 text-sage-dark" aria-hidden="true" data-id="element-325" data-mp="5zpcowe0gbcp" />
              {t({
            en: 'Your information is safe with us. We never share your details.',
            es: 'Tu información está segura con nosotros. Nunca compartimos tus datos.'
          })}
            </p>
          </div>
        </motion.form>}
    </AnimatePresence>;
}